import React, { useState, useEffect } from "react";
import { useAuth } from "hooks/useAuth";
import { osirisApi } from "services/api/osiris";
import { Row, Col, Carousel } from "react-bootstrap";
import { Spinner } from "react-bootstrap";
import EmptyCard from "components/common/pyramid/EmptyCard";
import PyramidCard from "components/common/pyramid/PyramidCard";
import BreakdownByBucket from "../computation/charts/BreakdownByBucket";
import BreakdownBySector from "../computation/charts/BreakdownBySector";
import BreakdownByCountry from "../computation/charts/BreakdownByCountry";
import WaterfallEffects from "../variation-analysis/charts/WaterfallEffects";
import { getClientPalette, defaultPalette } from "config/clientPalettes";
import LastFiveEvents from "../../../common/pyramid/LastFiveEvents";
import EmptyCardVariation from "components/common/pyramid/EmptyCardVariation";


const hasChartData = (chartData) => {
  const hasComputationData = !!chartData.HasComputationCharts;
  const hasVariationAnalysisData = !!chartData.HasVariationCharts;

  return {
    hasComputationData,
    hasVariationAnalysisData,
    hasAnyData: hasComputationData || hasVariationAnalysisData,
  };
};

/**
 * NoDataCard Component
 * Displays a card when no significant data is available
 */
const NoDataCard = () => {
  return (
    <Row className="g-3 mb-3">
      <Col md={12}>
        <PyramidCard className="h-100">
          <div className="text-center py-5">
            <h4 className="text-muted mb-3">No Data Available</h4>
            <p className="text-muted">
              No computation or variation analysis events have been processed
              yet.
            </p>
          </div>
        </PyramidCard>
      </Col>
    </Row>
  );
};

/**
 * CarouselSection Component
 *
 * Displays a carousel containing ECL gap information
 * Shows both regular gap data and absolute gap data in alternating slides
 *
 * @param {Object} eclGapData - ECL gap data (can be positive or negative)
 * @param {Object} eclGapAbsData - Absolute ECL gap data (magnitude only)
 */
const CarouselSection = ({ eclGapData, eclGapAbsData }) => {
  return (
    <>
      {/* Carousel for ECL Gap Data */}
      <Col md={4}>
        <PyramidCard className="h-100">
          <Carousel
            variant="dark"
            interval={3000} // Auto-rotate every 3 seconds
            controls={true} // Show navigation controls
            indicators={false} // Hide slide indicators
          >
            <Carousel.Item>
              <EmptyCard chartData={eclGapData} />
            </Carousel.Item>
            <Carousel.Item>
              <EmptyCard badgeColor="info" chartData={eclGapAbsData} />
            </Carousel.Item>
          </Carousel>
        </PyramidCard>
      </Col>
    </>
  );
};

/**
 * DashboardCharts Component
 *
 * Main dashboard component that provides a comprehensive overview of ECL data
 * Combines data from various sources: recent events, computations, and variation analysis
 * This serves as the central hub for monitoring financial risk metrics
 */
const DashboardCharts = () => {
  // State for storing all chart data fetched from the API
  const [chartData, setChartData] = useState({
    LastComputationGroupId: null,
    LastVariationAnalysisGroupId: null,
    LastFiveEventsData: [],
    LastComputationDate: [],
    EclGroupData: null,
    EclOsirisData: null,
    EclGapData: null,
    EclGapAbsData: null,
    BucketData: [],
    SectorData: [],
    CountryData: [],
    LastVariationAnalysisDates: [],
    EclDepartureData: null,
    EclArrivalData: null,
    EclVariationData: null,
    WaterfallData: [],
  });

  const [isLoading, setIsLoading] = useState(true);
  const [currentPalette, setCurrentPalette] = useState(defaultPalette);
  const { accessToken, account, userList, companyList } = useAuth();
  const api = osirisApi();

  useEffect(() => {
    if (account && companyList && companyList.length > 0) {
      const newPalette = getClientPalette(companyList[0].id || "default");
      setCurrentPalette(newPalette);
    }
  }, [account, companyList]);

  useEffect(() => {
    const fetchDashboardCharts = async () => {
      if (!accessToken) return;

      try {
        setIsLoading(true);

        const data = await api.getDashboardCharts();

        if (data) {
          const transformedData = {
            HasComputationCharts:
              Array.isArray(data.ComputationCharts?.charts) &&
              data.ComputationCharts.charts.length > 0,
            HasVariationCharts:
              Array.isArray(data.VariationAnalysisCharts?.charts) &&
              data.VariationAnalysisCharts.charts.length > 0,
            LastComputationGroupId: data.ComputationCharts?.groupId || null,
            LastVariationAnalysisGroupId:
              data.VariationAnalysisCharts?.groupId || null,
            LastFiveEventsData: data.LastFiveEvents || [],

            LastComputationDate:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "LastComputationDate"
              )?.Data || [],

            EclGroupData:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "EclTotalSumInv"
              )?.Data?.[0] || null,

            EclOsirisData:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "EclTotalSumOsiris"
              )?.Data?.[0] || null,

            EclGapData:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "EclGapTotal"
              )?.Data?.[0] || null,

            EclGapAbsData:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "EclGapTotalAbs"
              )?.Data?.[0] || null,

            BucketData:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "BreakdownByBucket"
              )?.Data?.map((item) => ({
                name: item.Bucket,
                value: item.EclOsiris,
              })) || [],

            SectorData:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "BreakdownBySector"
              )?.Data?.map((item) => ({
                name: item.Sector,
                value: item.EclOsiris,
              })) || [],

            CountryData:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "BreakdownByCountry"
              )?.Data?.map((item) => ({
                name: item.Country,
                bucket1: item.EclBucket1,
                bucket2: item.EclBucket2,
              })) || [],

            LastVariationAnalysisDates:
              data.VariationAnalysisCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "LastVariationAnalysisDates"
              )?.Data || [],

            EclDepartureData:
              data.VariationAnalysisCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "SumEclDeparture"
              )?.Data?.[0] || null,

            EclArrivalData:
              data.VariationAnalysisCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "SumEclArrival"
              )?.Data?.[0] || null,

            EclVariationData:
              data.VariationAnalysisCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "SumEclVariation"
              )?.Data?.[0] || null,

            WaterfallData:
              data.VariationAnalysisCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "WaterfallEffects"
              )?.Data || [],
          };
          setChartData(transformedData);
        }
      } catch (error) {
        console.error("Error retrieving dashboard charts:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardCharts();
  }, [accessToken]);

  /**
   * Component render - Conditional dashboard display based on event types
   * Shows different sections based on what types of events have occurred
   */
  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  // Analyze the events to determine what to display
  const eventAnalysis = hasChartData(chartData);

  return (
    <div>
      {/* Always show Recent Activity Section */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <LastFiveEvents
            title="Last 5 Events on Osiris"
            data={chartData.LastFiveEventsData}
            userList={userList}
          />
        </Col>
      </Row>

      {/* Conditional rendering based on event analysis */}
      {eventAnalysis.hasAnyData ? (
        <>
          {/* Case 2: Has computation events - show computation-related cards */}
          {eventAnalysis.hasComputationData && (
            <>
              {/* Computation Metadata - Shows dates of recent computation runs */}
              <Row className="g-3 mb-3">
                <Col md={12}>
                  <EmptyCard
                    lastRecordDateData={chartData.LastComputationDate}
                  />
                </Col>
              </Row>

              {/* ECL Overview Section - Shows CSA, Osiris and gap data */}
              <Row className="g-3 mb-3">
                <Col md={4}>
                  <EmptyCard
                    chartData={
                      chartData.EclGroupData && companyList.length > 0
                        ? {
                          ...chartData.EclGroupData,
                          Title: `ECL - ${companyList.find(
                            (company) =>
                              company.id ===
                              chartData.LastComputationGroupId
                          )?.displayName
                            }`,
                        }
                        : chartData.EclGroupData
                    }
                  />
                </Col>
                <Col md={4}>
                  <EmptyCard chartData={chartData.EclOsirisData} />
                </Col>
                <CarouselSection
                  eclGapData={chartData.EclGapData}
                  eclGapAbsData={chartData.EclGapAbsData}
                />
              </Row>

              {/* ECL Breakdown Section - Shows data by bucket, sector and country */}
              <Row className="g-3 mb-3">
                <Col md={4}>
                  <BreakdownByBucket
                    data={chartData.BucketData}
                    palette={currentPalette}
                  />
                </Col>
                <Col md={4}>
                  <BreakdownBySector
                    data={chartData.SectorData}
                    palette={currentPalette}
                  />
                </Col>
                <Col md={4}>
                  <BreakdownByCountry
                    data={chartData.CountryData}
                    palette={currentPalette}
                  />
                </Col>
              </Row>
            </>
          )}

          {/* Case 3: Has variation analysis events - show variation analysis cards */}
          {eventAnalysis.hasVariationAnalysisData && (
            <>
              {/* Variation Analysis Metadata - Shows dates of recent variation analyses */}
              <Row className="g-3 mb-3">
                <Col md={12}>
                  <EmptyCard
                    lastRecordDateData={chartData.LastVariationAnalysisDates}
                  />
                </Col>
              </Row>

              {/* Variation Analysis Overview - Shows ECL changes over time */}
              <Row className="g-3 mb-3">
                <Col md={4}>
                  <EmptyCard chartData={chartData.EclDepartureData} />
                </Col>
                <Col md={4}>
                  <EmptyCard chartData={chartData.EclArrivalData} />
                </Col>
                <Col md={4}>
                  <EmptyCardVariation eclData={chartData.EclVariationData} />
                </Col>
              </Row>

              {/* Detailed Variation Analysis - Shows factors causing ECL changes */}
              <Row className="g-3 mb-3">
                <Col md={12}>
                  <WaterfallEffects
                    data={chartData.WaterfallData}
                    palette={currentPalette}
                  />
                </Col>
              </Row>
            </>
          )}
        </>
      ) : (
        // Case 1: No events or only "Data Preparation" events - show minimal card
        <NoDataCard />
      )}
    </div>
  );
};

export default DashboardCharts;
